import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { SERVICES, FAQS } from "@/data/site";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/motion/Reveal";
import Icon from "@/components/Icon";
import PageHero from "@/components/PageHero";
import FAQSection from "@/components/FAQSection";
import CTASection from "@/components/CTASection";

const WHY = [
  { t: "Personalized Plans", d: "Every treatment plan is built around your history, lifestyle and goals." },
  { t: "Non-Invasive First", d: "The least invasive, most precise diagnostics and management, always." },
  { t: "Experienced Physician", d: "KGMU-trained consultant and National Chairman, Cardiabcon." },
  { t: "Patient Education", d: "You leave understanding your heart and the reason behind every step." },
];

export default function Services() {
  return (
    <>
      <PageHero
        crumb="Services"
        eyebrow="Cardiology Services"
        titleLines={["Comprehensive", "Cardiology Services", "in Varanasi"]}
        subtitle="Expert consultation and management for all your heart health needs — from prevention to precise diagnostic review."
      />

      <section className="pb-24 md:pb-32 bg-paper" data-testid="services-list">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <StaggerGroup className="grid grid-cols-1 md:grid-cols-2 gap-px bg-line border border-line rounded-3xl overflow-hidden">
            {SERVICES.map((s, i) => (
              <StaggerItem key={s.key} data-testid={`service-card-${s.key}`}>
                <div className="group bg-surface p-8 md:p-10 h-full flex gap-6 transition-colors duration-300 hover:bg-mint/40">
                  <span className="shrink-0 grid place-items-center w-14 h-14 rounded-2xl bg-mint text-brand transition-colors duration-300 group-hover:bg-brand group-hover:text-white">
                    <Icon name={s.icon} size={24} />
                  </span>
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-display text-sm text-brand/60">{String(i + 1).padStart(2, "0")}</span>
                    </div>
                    <h3 className="font-display text-2xl md:text-3xl text-ink mb-2">{s.title}</h3>
                    <p className="text-sm md:text-base text-sage leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Why choose our services */}
      <section className="py-24 md:py-32 bg-brand-deep text-white grain relative overflow-hidden" data-testid="services-why">
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.22em] font-bold text-brand mb-5">Why Choose Our Services</p>
            <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight font-light max-w-3xl mb-14">
              Care designed around you — not the other way around.
            </h2>
          </Reveal>
          <StaggerGroup className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {WHY.map((w, i) => (
              <StaggerItem key={w.t}>
                <div className="rounded-3xl border border-white/12 p-8 h-full">
                  <span className="font-display text-5xl text-brand">0{i + 1}</span>
                  <h3 className="font-display text-2xl mt-4 mb-2">{w.t}</h3>
                  <p className="text-sm text-mint/75 leading-relaxed">{w.d}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
          <Reveal delay={0.1}>
            <Link to="/contact" className="mt-12 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.16em] text-brand hover:text-white transition-colors">
              Book a heart check-up <ArrowUpRight size={17} />
            </Link>
          </Reveal>
        </div>
      </section>

      <FAQSection items={FAQS.services} heading="Service Questions, Answered" />

      {/* General Care callout */}
      <section className="py-20 md:py-24 bg-paper border-t border-line" data-testid="services-general-callout">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <p className="text-xs uppercase tracking-[0.22em] font-bold text-brand mb-4">Beyond Cardiology</p>
            <h2 className="font-display text-3xl md:text-4xl leading-tight text-ink font-light text-balance">
              Dr. Pallavi Mishra also offers General Physician care.
            </h2>
            <p className="mt-4 text-lg text-sage leading-relaxed max-w-2xl">
              Fever, seasonal illness, diabetes, BP, thyroid, digestive concerns and preventive check-ups — everyday health, thoughtfully handled by the same trusted doctor.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link to="/general-care" className="inline-flex items-center gap-2 rounded-full bg-brand hover:bg-brand-dark text-white px-7 py-4 text-sm font-semibold uppercase tracking-[0.16em] transition-colors duration-300">
              Explore General Care <ArrowUpRight size={17} />
            </Link>
          </div>
        </div>
      </section>

      <CTASection title="Book Your Cardiology Consultation" />
    </>
  );
}
